import { Page } from "@/components";
import {
  Card,
  Container,
  Flex,
  Grid,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@suns/design-system";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAsync } from "@/shared/hooks";
import { SunsApiError } from "@suns/api";
import {
  ContactInfo,
  ContactInfoView,
} from "@/components/ContactInfo/ContactInfo";
import { useState } from "react";
import { DataTable } from "@suns/design-system/src/components/DataTable/DataTable";
import { SortingState } from "@tanstack/react-table";
import { agentByIdLoader } from "./loaders/agency-loader";
import { playersLoader } from "../players/loaders/player-loader";
import { agentRosterColumns } from "../players/players-columns";
import { EntityIntelListings } from "@/components/EntityIntelListings/EntityIntelListing";
import { apolloApi } from "@/shared/api";

export interface AgentInfo {
  email: string | undefined;
  mobilePhone: string | undefined;
  workPhone: string | undefined;
}

export function AgentView() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [sortingColumns, setSortingColumns] = useState<SortingState>([
    {
      id: "name",
      desc: false,
    },
  ]);
  const [paginationValues, setPaginationValues] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const pagination = {
    pageIndex: paginationValues.pageIndex,
    pageSize: paginationValues.pageSize,
  };

  const {
    response: agent,
    loading: agentLoading,
    error: agentError,
  } = useAsync(agentByIdLoader, {
    id: Number(agentId),
  });

  const { response, loading } = useAsync(playersLoader, {
    agentId: Number(agentId),
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    sortColumn: sortingColumns[0]?.id,
    sortDir: sortingColumns[0]?.desc ? "DESC" : "ASC",
  });

  const handleSelectPlayer = (id: number | null) => {
    if (!id) return;
    navigate(`/players/${id}`);
  };

  const handleUpsertContactInfo = async (contactInfo: ContactInfo) => {
    const upsertResponse = await apolloApi.upsertAgent(contactInfo);
    return upsertResponse.agent;
  };

  if (agentError) {
    throw new SunsApiError("Error loading agent", {
      cause: agentError,
    });
  }

  return (
    <Page
      title={agent ? agent.agent.name : "View Agent"}
      loading={agentLoading}
      loadingState={<AgentViewLoading />}
      breadcrumbPaths={[
        { label: "Players", link: "/players" },
        { label: "Agents", link: "/players/agents" },
      ]}
      size="sm"
      render={() => {
        if (!agent) throw new Error("Unable to load agent");

        return (
          <Container size="xl">
            <Flex direction="down" gap="md">
              <>
                <Card>
                  <Flex direction="down" gap="md">
                    <Flex direction="down" gap="xs">
                      <Text heading size="2xl">
                        {agent.agent.name}
                      </Text>
                      <Link
                        className="text-sm font-bold text-primary"
                        to={`/players/agents?agencyId=${agent.agent.agency?.id}`}
                        state={{
                          agencyId: agent.agent.agency?.id,
                        }}
                      >
                        {agent.agent.agency?.name}
                      </Link>
                    </Flex>
                    <ContactInfoView
                      contactInfo={agent.agent}
                      onSubmit={handleUpsertContactInfo}
                    />
                  </Flex>
                </Card>
              </>
              <Card>
                <Tabs defaultValue="players">
                  <TabsList className="mb-4 max-sm:w-full" wrap>
                    <TabsTrigger value="players">Players</TabsTrigger>
                    <TabsTrigger value="intel">Intel</TabsTrigger>
                  </TabsList>
                  <TabsContent value="players">
                    <DataTable
                      loading={loading}
                      columns={agentRosterColumns}
                      data={response?.players || []}
                      count={response?.count || 0}
                      pagination={pagination}
                      setPagination={(setPagination) => {
                        const newPagination =
                          typeof setPagination === "function"
                            ? setPagination(pagination)
                            : setPagination;

                        setPaginationValues(newPagination);
                      }}
                      sorting={sortingColumns}
                      setSorting={(setSorting) => {
                        const newSort =
                          typeof setSorting === "function"
                            ? setSorting(sortingColumns)
                            : setSorting;

                        const sort = newSort[0] ?? null;

                        if (sort) {
                          setSortingColumns([
                            {
                              id: sort.id,
                              desc: sort.desc,
                            },
                          ]);
                        }
                      }}
                      onRowClicked={(row) =>
                        handleSelectPlayer(row.original.id)
                      }
                    />
                  </TabsContent>
                  <TabsContent value="intel">
                    <EntityIntelListings />
                  </TabsContent>
                </Tabs>
              </Card>
            </Flex>
          </Container>
        );
      }}
    />
  );
}

function AgentViewLoading() {
  return (
    <Container size="xl">
      <Flex direction="down" gap="md">
        <Card>
          <Flex direction="down" gap="md">
            <Flex direction="down" gap="sm">
              <Skeleton className="h-8 w-72" />
              <Skeleton className="h-4 w-48" />
            </Flex>
            <Flex gap="md">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </Flex>
            <Grid columns="3" gap="sm">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </Grid>
          </Flex>
        </Card>
      </Flex>
    </Container>
  );
}
